import { useApolloClient, useMutation, useQuery } from '@vue/apollo-composable'
import { computed, onScopeDispose, ref, shallowRef, watch } from 'vue'

import {
  GenerateMarketingPlanStatus,
  MarketingPlanSlotImageStatus,
  MarketingPlanSlotStatus,
  MarketingPlanSlotTextStatus,
} from '@/generated/graphql'

import { reportError, reportOutcomeOnce } from '../lib/reportError'
import ACCEPT_MARKETING_PLAN_OUTLINE from '../queries/acceptMarketingPlanOutline.gql'
import ADD_MARKETING_PLAN_SLOT from '../queries/addMarketingPlanSlot.gql'
import APPROVE_MARKETING_PLAN_SLOTS from '../queries/approveMarketingPlanSlots.gql'
import DELETE_MARKETING_PLAN from '../queries/deleteMarketingPlan.gql'
import DELETE_MARKETING_PLAN_SLOTS from '../queries/deleteMarketingPlanSlots.gql'
import GENERATE_MARKETING_PLAN from '../queries/generateMarketingPlan.gql'
import GENERATE_SLOT_IMAGE from '../queries/generateSlotImage.gql'
import GET_MARKETING_PLAN from '../queries/getMarketingPlan.gql'
import RECREATE_SLOT_TEXT from '../queries/recreateSlotText.gql'
import UPDATE_SLOT_SCHEDULE from '../queries/updateMarketingPlanSlotSchedule.gql'
import UPDATE_SLOT_TEXT from '../queries/updateSlotText.gql'

const POLL_INTERVAL_MS = 4000

/**
 * Loads the company's current marketing plan and keeps it fresh while the backend
 * is still generating slot text. Text is produced out of band (see backend §6), so
 * the calendar fills progressively: we poll only while at least one slot is still
 * GENERATING and stop as soon as every slot has resolved.
 */
export function useMarketingPlan() {
  const { client } = useApolloClient()

  // Set to NO_TOPICS / NO_PLATFORMS when the last generate returned an empty state.
  const emptyReason = ref<string | null>(null)

  // Drives Apollo's polling. Kept as its own ref (rather than reading isGenerating
  // inside the options callback) because that callback runs synchronously during
  // useQuery init — before isGenerating is declared — which would hit its TDZ.
  const pollInterval = ref(0)

  const { result, loading, error, refetch } = useQuery(
    GET_MARKETING_PLAN,
    () => ({ id: null }),
    () => ({
      pollInterval: pollInterval.value,
      fetchPolicy: 'cache-and-network',
      // Deliver errors through the result instead of the throwing error-subscription.
      // The latter makes @vue/apollo-composable call Apollo's deprecated getLastResult
      // /getLastError on every error (noisy while the backend resolver isn't live yet).
      errorPolicy: 'all',
    })
  )

  // A failed poll can deliver `plan: null`. Keep showing the last plan the server
  // returned, so the calendar doesn't empty out and polling (which depends on the
  // plan's slots) keeps going. Cleared when the plan is deleted on purpose.
  const lastPlan = shallowRef<{ id?: string } | null>(null)
  watch(result, (value) => {
    if (value?.plan) lastPlan.value = value.plan
    else if (value && !error.value) lastPlan.value = null
  })

  const plan = computed(() => {
    const current = result.value?.plan
    if (current) return current
    return error.value ? lastPlan.value : null
  })

  // Report a load failure once per error, not on every 4s poll.
  watch(error, (current, previous) => {
    if (current && !previous) {
      reportError('load-plan', current, { planId: lastPlan.value?.id ?? null })
    }
  })

  // True only before the first answer from the server. Later polls don't flash a loader.
  const initialLoading = computed(() => loading.value && !result.value && !error.value)
  const loadError = computed(() => error.value ?? null)

  /** Manual reload for the retry button. Throws so the caller can count the attempt. */
  async function reload() {
    const res = await refetch()
    if (res?.errors?.length) throw res.errors[0]
    return res
  }
  const planId = computed(() => plan.value?.id ?? null)
  const slots = computed(() => plan.value?.slots ?? [])
  const hasPlan = computed(() => !!plan.value)

  const isGenerating = computed(() =>
    slots.value.some(
      (slot: { textStatus?: string }) => slot.textStatus === MarketingPlanSlotTextStatus.Generating
    )
  )

  // Image requests stay open after the text is saved. Poll until each one
  // reaches success, failure, or timeout.
  const imagesPending = computed(() =>
    slots.value.some(
      (slot: { textStatus?: string; imageStatus?: string | null }) =>
        slot.textStatus !== MarketingPlanSlotTextStatus.Draft &&
        slot.imageStatus === MarketingPlanSlotImageStatus.Pending
    )
  )

  const scheduleError = computed(() => plan.value?.scheduleError || null)

  // The schedule worker has not written slots yet. Poll until they arrive or the
  // call fails. An empty slot list is not an outline.
  const schedulePending = computed(
    () => hasPlan.value && slots.value.length === 0 && !scheduleError.value
  )

  // Backend failures arrive through polling as statuses, not as errors. Report each
  // failed slot or schedule once per session so Sentry shows how often generation fails.
  watch(
    () => [plan.value?.id, plan.value?.scheduleError, slots.value] as const,
    ([currentPlanId, currentScheduleError, currentSlots]) => {
      if (!currentPlanId) return
      if (currentScheduleError) {
        reportOutcomeOnce(`schedule:${currentPlanId}`, 'schedule-failed', {
          planId: currentPlanId,
          reason: currentScheduleError,
        })
      }
      for (const slot of currentSlots as Array<{
        id: string
        textStatus?: string
        imageStatus?: string | null
        generationError?: string | null
      }>) {
        if (slot.textStatus === MarketingPlanSlotTextStatus.Failed) {
          reportOutcomeOnce(`text:${slot.id}`, 'slot-text-failed', {
            planId: currentPlanId,
            slotId: slot.id,
            reason: slot.generationError ?? null,
          })
        }
        if (
          slot.imageStatus === MarketingPlanSlotImageStatus.Failed ||
          slot.imageStatus === MarketingPlanSlotImageStatus.TimedOut
        ) {
          reportOutcomeOnce(`image:${slot.id}`, 'slot-image-failed', {
            planId: currentPlanId,
            slotId: slot.id,
            code: slot.imageStatus,
          })
        }
      }
    }
  )

  // A hidden tab doesn't need fresh data. Polling resumes, with an immediate
  // refetch, when the user comes back.
  const pageVisible = ref(typeof document === 'undefined' || document.visibilityState !== 'hidden')
  function onVisibilityChange() {
    pageVisible.value = document.visibilityState !== 'hidden'
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange)
    onScopeDispose(() => document.removeEventListener('visibilitychange', onVisibilityChange))
  }

  const workInFlight = computed(
    () => isGenerating.value || imagesPending.value || schedulePending.value
  )

  // Poll while the schedule, slot text, or an image request is still in flight.
  watch(
    [workInFlight, pageVisible],
    ([inFlight, visible], previous) => {
      pollInterval.value = inFlight && visible ? POLL_INTERVAL_MS : 0
      const becameVisible = visible && previous && !previous[1]
      // A failed refetch lands in `error`, which is reported and shown already.
      if (inFlight && becameVisible) refetch()?.catch(() => {})
    },
    { immediate: true }
  )

  const { mutate: generateMutation, loading: generating } = useMutation(GENERATE_MARKETING_PLAN)

  async function generate({
    startDate = null,
    topicId = null,
    windowDays = null,
  }: {
    startDate?: string | null
    topicId?: string | null
    windowDays?: number | null
  } = {}) {
    emptyReason.value = null
    const input: { startDate?: string; topicId?: string; windowDays?: number } = {}
    if (startDate) input.startDate = startDate
    if (topicId) input.topicId = topicId
    if (windowDays) input.windowDays = windowDays
    const res = await generateMutation({ input })
    const status = res?.data?.result?.status
    const generatedPlanId: string | null = res?.data?.result?.plan?.id ?? null

    if (
      status === GenerateMarketingPlanStatus.NoTopics ||
      status === GenerateMarketingPlanStatus.NoPlatforms
    ) {
      emptyReason.value = status
    }

    return { status, planId: generatedPlanId }
  }

  // Slot mutations use the global mutate fetchPolicy (no-cache), so results are
  // not merged automatically — patch the normalized MarketingPlanSlot in cache.
  const { mutate: recreateMutation } = useMutation(RECREATE_SLOT_TEXT)
  const { mutate: updateMutation } = useMutation(UPDATE_SLOT_TEXT)
  const { mutate: deleteMutation } = useMutation(DELETE_MARKETING_PLAN_SLOTS)
  const { mutate: generateImageMutation } = useMutation(GENERATE_SLOT_IMAGE)
  const { mutate: approveMutation } = useMutation(APPROVE_MARKETING_PLAN_SLOTS)
  const { mutate: deletePlanMutation } = useMutation(DELETE_MARKETING_PLAN)
  const { mutate: updateScheduleMutation } = useMutation(UPDATE_SLOT_SCHEDULE)
  const { mutate: addSlotMutation } = useMutation(ADD_MARKETING_PLAN_SLOT)
  const { mutate: acceptOutlineMutation, loading: acceptingOutline } = useMutation(
    ACCEPT_MARKETING_PLAN_OUTLINE
  )

  const isOutline = computed(
    () =>
      hasPlan.value &&
      slots.value.length > 0 &&
      slots.value.every(
        (slot: { textStatus?: string }) => slot.textStatus === MarketingPlanSlotTextStatus.Draft
      )
  )

  async function updateSlotSchedule(
    slotId: string,
    schedule: { platform: string; date: string; time: string }
  ) {
    await updateScheduleMutation({
      input: { planId: planId.value, slotId, ...schedule },
    })
  }

  async function addSlot(schedule: { platform: string; date: string; time: string }) {
    await addSlotMutation({
      input: { planId: planId.value, ...schedule },
    })
  }

  async function acceptOutline() {
    await acceptOutlineMutation({ input: { planId: planId.value } })
  }

  function mergeSlotIntoPlanCache(slot: Record<string, unknown> | null | undefined) {
    const slotId = slot?.id
    if (!slotId || typeof slotId !== 'string') return

    const fields: Record<string, () => unknown> = {}
    for (const [key, value] of Object.entries(slot)) {
      if (key === 'id' || key === '__typename') continue
      fields[key] = () => value
    }

    client.cache.modify({
      id: `MarketingPlanSlot:${slotId}`,
      fields,
    })
  }

  // Pending slots that are ready to be scheduled (the Approve-All target set).
  // A slot counts when it still has text and has not been approved. textStatus
  // READY is the normal case; generated text alone is enough if status was not
  // written back as PENDING.
  const approvableSlots = computed(() =>
    slots.value.filter((slot: { status?: string; textStatus?: string; generatedText?: string }) => {
      if (
        slot.status === MarketingPlanSlotStatus.Approved ||
        slot.status === MarketingPlanSlotStatus.Discarded
      )
        return false
      if (
        slot.textStatus === MarketingPlanSlotTextStatus.Generating ||
        slot.textStatus === MarketingPlanSlotTextStatus.Failed
      ) {
        return false
      }
      return Boolean(slot.generatedText?.trim())
    })
  )

  /** Re-roll one slot's text (also the retry path for FAILED slots). */
  async function recreateSlotText(slotId: string) {
    const res = await recreateMutation({ input: { planId: planId.value, slotId } })
    const slot = res?.data?.slot ?? null
    mergeSlotIntoPlanCache(slot)
    return slot
  }

  /** Save edited text for one slot (text-only — platform/time stay locked). */
  async function updateSlotText(slotId: string, text: string) {
    const res = await updateMutation({ input: { planId: planId.value, slotId, text } })
    const slot = res?.data?.slot ?? null
    mergeSlotIntoPlanCache(slot)
    return slot
  }

  /** Generate a preview image for one slot and patch imageUrl into the cached plan. */
  async function generateSlotImage(slotId: string) {
    const res = await generateImageMutation({ input: { planId: planId.value, slotId } })
    const slot = res?.data?.slot ?? null
    mergeSlotIntoPlanCache(slot)
    return slot
  }

  /**
   * Delete slots. Approved slots fan out to the existing delete/dequeue path on the
   * backend; this doubles as the unapprove action. Refetch so changes hit the calendar.
   */
  async function deleteSlots(slotIds: string[]) {
    const res = await deleteMutation({ input: { planId: planId.value, slotIds } })
    return res?.data?.results ?? []
  }

  /**
   * Approve slots → images generated + posts scheduled server-side (partial success is
   * expected). Refetch to pick up new APPROVED statuses / savedContentIds.
   * Returns the per-slot [SlotOperationResult] contract.
   */
  async function approveSlots(slotIds: string[]) {
    const res = await approveMutation({ input: { planId: planId.value, slotIds } })
    return res?.data?.results ?? []
  }

  /** Drop this one plan. A remaining plan (current or a later iteration) is loaded again. */
  async function deletePlan(id) {
    const deletedPlanId = id || planId.value

    await deletePlanMutation({ input: { planId: deletedPlanId } })
    pollInterval.value = 0
    lastPlan.value = null
    if (deletedPlanId) {
      client.cache.evict({ id: `MarketingPlan:${deletedPlanId}` })
      client.cache.gc()
    }
  }

  return {
    plan,
    planId,
    slots,
    hasPlan,
    approvableSlots,
    loading,
    error,
    initialLoading,
    loadError,
    reload,
    generating,
    isGenerating,
    imagesPending,
    schedulePending,
    scheduleError,
    isOutline,
    acceptingOutline,
    emptyReason,
    generate,
    recreateSlotText,
    updateSlotText,
    generateSlotImage,
    deleteSlots,
    approveSlots,
    deletePlan,
    updateSlotSchedule,
    addSlot,
    acceptOutline,
    refetch,
  }
}
